import { normalizeMarker } from './domain.js';
/**
 * Alpha's lower thirds (ADR 083): the station's own designs for its programmes, each a 1920×1080 picture with
 * transparency (`apps/web/public/straps/<key>.png`, the preview's "Preview Version" watermark, date and clock taken
 * out), with the boxes AirPost writes a strap's lines in and, where the design shows one, today's date or the time.
 * Boxes are in pixels of the 1920×1080 frame; a line is centred on its box's height and starts at its left (or is
 * centred, or ends at its right). A strap of the other CG templates (credit, locator, LIVE, full screen) keeps
 * AirPost's schematic: no design was given for them.
 */
export const STRAP_DESIGN_KEYS = ['news', 'kalimera', 'enimerosi', 'christiana'];
const box = (x, y, w, h, size, weight, color, align = 'left', upper = false) => ({ x, y, w, h, size, weight, color, align, upper });
// measured on the pictures: the bars' edges, the digits of the preview's date and clock (their height gives the size)
export const STRAP_DESIGNS = [
    {
        key: 'news',
        name: 'Alpha News',
        src: '/straps/news.png',
        area: { x: 93, y: 870, w: 1734, h: 178 },
        programmes: ['ALPHA NEWS'],
        // the red bar 871–987 after the A; the white bar 996–1047 after NEWS, the date at its right end
        one: box(262, 871, 1530, 116, 56, 700, '#ffffff', 'left', true),
        two: [box(262, 871, 1530, 116, 54, 700, '#ffffff', 'left', true), box(262, 996, 1318, 51, 30, 600, '#c6243c')],
        date: { ...box(1600, 996, 201, 49, 50, 700, '#c6243c', 'right'), prefix: '• ' },
        clock: null,
    },
    {
        key: 'kalimera',
        name: 'Alpha Καλημέρα',
        src: '/straps/kalimera.png',
        area: { x: 94, y: 874, w: 1734, h: 166 },
        programmes: ['ΚΑΛΗΜΕΡΑ'],
        // the white box 924–999; the thin bar 1008–1039 after its orange and blue; the clock in the logo's yellow band
        one: box(300, 924, 1505, 75, 46, 700, '#1d1d1b', 'left', true),
        two: [box(300, 924, 1505, 75, 44, 700, '#1d1d1b', 'left', true), box(580, 1008, 1225, 31, 24, 600, '#1d1d1b')],
        date: null,
        clock: box(94, 1003, 162, 32, 32, 600, '#ffffff', 'center'),
    },
    {
        key: 'enimerosi',
        name: 'Alpha Ενημέρωση',
        src: '/straps/enimerosi.png',
        area: { x: 86, y: 840, w: 1748, h: 206 },
        programmes: ['ΕΝΗΜΕΡΩΣΗ'],
        // the grey bar 889–1001 after the logo; the light bar 1005–1045; the date in the red box under the logo
        one: box(290, 889, 1505, 112, 52, 700, '#ffffff', 'left', true),
        two: [box(290, 889, 1505, 112, 50, 700, '#ffffff', 'left', true), box(290, 1005, 1510, 40, 26, 600, '#2b2b2b')],
        date: { ...box(121, 1006, 138, 37, 30, 700, '#ffffff', 'center'), prefix: '' },
        clock: null,
    },
    {
        key: 'christiana',
        name: 'Με αγάπη Χριστιάνα',
        src: '/straps/christiana.png',
        area: { x: 66, y: 866, w: 1804, h: 160 },
        programmes: ['ΧΡΙΣΤΙΑΝΑ'],
        // the white bar 917–1015 under the hearts' strip, after the logo and before the slanted end
        one: box(470, 917, 1270, 98, 46, 700, '#2b2b2b', 'left', true),
        two: [box(470, 919, 1270, 52, 40, 700, '#2b2b2b', 'left', true), box(470, 969, 1240, 44, 30, 600, '#d24f80')],
        date: null,
        clock: null,
    },
];
export const DEFAULT_STRAP_DESIGN = 'news';
export const strapDesign = (key) => STRAP_DESIGNS.find((d) => d.key === key) ?? STRAP_DESIGNS.find((d) => d.key === DEFAULT_STRAP_DESIGN);
/** The design of a programme's straps: `6:45-ALPHA ΚΑΛΗΜΕΡΑ` (and its `-SPK`) → Καλημέρα; none known → News. */
export function strapDesignFor(programmeName) {
    const name = normalizeMarker(programmeName ?? '');
    return STRAP_DESIGNS.find((d) => d.key !== DEFAULT_STRAP_DESIGN && d.programmes.some((w) => name.includes(w))) ?? strapDesign(DEFAULT_STRAP_DESIGN);
}
/** Whether a CG template's straps are drawn in the station's designs (the lower thirds) or as AirPost's schematic. */
export const strapTemplateHasDesign = (template) => template === 'lower-third-1' || template === 'lower-third-2';
/** The boxes of a strap's lines in a design: one line, or two when the second has words. */
export function strapLineBoxes(d, lines) {
    const words = lines.map((l) => l.trim());
    if (words.length >= 2 && words[1])
        return [{ text: words[0] ?? '', box: d.two[0] }, { text: words[1], box: d.two[1] }];
    return [{ text: words[0] ?? '', box: d.one }];
}
/** Capitals as a Greek headline has them: no accents (the diaeresis stays), the final sigma a Σ. */
export function strapCaps(text) {
    return text
        .toUpperCase()
        .normalize('NFD')
        .replace(/([Ͱ-Ͽἀ-῿]̈?)[̀́̓̔͂ͅ]+/g, '$1')
        .normalize('NFC');
}
const nicosia = (now) => {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Nicosia', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
    const of = (t) => parts.find((p) => p.type === t)?.value ?? '00';
    return { day: of('day'), month: of('month'), hour: of('hour'), minute: of('minute') };
};
/** The day in Cyprus, as the designs write it: `03/11`. */
export function strapDate(now) {
    const n = nicosia(now);
    return `${n.day}/${n.month}`;
}
/** The time in Cyprus: `19:13`. */
export function strapClock(now) {
    const n = nicosia(now);
    return `${n.hour}:${n.minute}`;
}
//# sourceMappingURL=strap-designs.js.map